import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../lib/session';
import { Alert } from './ui';
import { useT } from '../i18n/locale';
import { ROLE_LABEL } from '../api/enums';
import type { UserRole } from '../api/enums';

/**
 * Hides a route from roles that cannot use it.
 *
 * Presentation only — the server rejects these calls regardless, and that is
 * the real boundary. This exists so a staff user who follows a stale link
 * gets a plain explanation instead of a page that renders and then fills with
 * 403s.
 */
export default function RequireRole({
  allow,
  children,
}: {
  allow: readonly UserRole[];
  children: ReactNode;
}) {
  const t = useT();
  const { user } = useSession();

  if (!user || !allow.includes(user.role)) {
    return (
      <div className="mx-auto w-full max-w-xl p-6">
        <Alert tone="warn">
          {t('role.restricted', { roles: allow.map((r) => t(ROLE_LABEL[r])).join(' / ') })}{' '}
          <Link to="/" className="font-bold underline">
            {t('role.backHome')}
          </Link>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
